import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, type OperatorProfile } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Card, Field, Loading, Pill } from '../components/ui'
import Appearance from '../components/Appearance'

export default function Settings() {
  const { can } = useAuth()

  const profiles = useQuery({
    queryKey: ['operator-profiles'],
    queryFn: () => api.get<OperatorProfile[]>('/api/admin/operator-profiles'),
  })

  return (
    <div className="stack" style={{ gap: 18 }}>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="page-sub">
            Operator profiles drive the USSD balance check. The seeded codes and patterns are placeholders — they
            must be confirmed against a real SIM before they mean anything.
          </div>
        </div>
      </div>

      <Appearance />

      <div className="banner warn">
        <div>
          <strong>Unverified operator data.</strong> The MCC/MNC values and balance USSD codes shipped with the
          system are best guesses. Read the real values from a live SIM (Devices → Run diagnostics reports the
          operator), paste a real balance response into the tester below, and mark the profile verified once it
          parses.
        </div>
      </div>

      {profiles.isLoading ? (
        <Loading />
      ) : (
        profiles.data?.map((p) => <ProfileCard key={p.id} profile={p} editable={can('Admin')} />)
      )}
    </div>
  )
}

function ProfileCard({ profile, editable }: { profile: OperatorProfile; editable: boolean }) {
  const qc = useQueryClient()
  const [form, setForm] = useState({
    balanceUssd: profile.balanceUssd ?? '',
    balanceRegex: profile.balanceRegex ?? '',
    expiryRegex: profile.expiryRegex ?? '',
    expiryDateFormat: profile.expiryDateFormat ?? '',
    ussdTimeoutSeconds: profile.ussdTimeoutSeconds,
    isVerified: profile.isVerified,
    costPerSms: profile.costPerSms,
  })
  // Held as the typed string rather than the array so that a half-finished edit does not keep
  // being reformatted under the cursor; it is split on save.
  const [prefixes, setPrefixes] = useState(profile.msisdnPrefixes.join(' '))
  const [sample, setSample] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/admin/operator-profiles/${profile.id}`, {
        name: profile.name,
        mccMnc: profile.mccMnc,
        msisdnPrefixes: prefixes.split(/[\s,]+/).filter(Boolean),
        ...form,
      }),
    onSuccess: () => {
      setSaved(true)
      setError(null)
      void qc.invalidateQueries({ queryKey: ['operator-profiles'] })
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'Could not save.'),
  })

  const test = useMutation({
    mutationFn: () =>
      api.post<{ success: boolean; amount: number | null; expiry: string | null; error: string | null }>(
        '/api/admin/operator-profiles/test-parse',
        {
          response: sample,
          balanceRegex: form.balanceRegex || null,
          expiryRegex: form.expiryRegex || null,
          expiryFormat: form.expiryDateFormat || null,
        },
      ),
  })

  return (
    <Card
      title={
        <span className="row" style={{ gap: 9 }}>
          {profile.name}
          {profile.isVerified ? <Pill tone="ok">verified</Pill> : <Pill tone="warn">unverified</Pill>}
          <span className="faint small mono">{profile.mccMnc.join(', ') || 'no MCC/MNC'}</span>
          <span className="faint small">
            {profile.msisdnPrefixes.length
              ? `${profile.msisdnPrefixes.length} number prefixes`
              : 'no number prefixes — on-net routing cannot see this network'}
          </span>
        </span>
      }
      actions={
        editable && (
          <button className="btn primary sm" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : saved ? 'Saved' : 'Save'}
          </button>
        )
      }
    >
      {error && <div className="banner danger">{error}</div>}

      <div className="grid cols-2" style={{ gap: 16 }}>
        <Field label="Balance USSD" hint="What the device dials, e.g. *123#.">
          <input
            className="mono"
            value={form.balanceUssd}
            onChange={(e) => setForm({ ...form, balanceUssd: e.target.value })}
            placeholder="*123#"
            disabled={!editable}
          />
        </Field>
        <Field label="Cost per SMS" hint="Used for the cost report and the 'sends remaining' estimate.">
          <input
            type="number"
            step="0.01"
            value={form.costPerSms ?? ''}
            onChange={(e) => setForm({ ...form, costPerSms: e.target.value === '' ? null : Number(e.target.value) })}
            disabled={!editable}
          />
        </Field>
      </div>

      <Field
        label="Number prefixes"
        hint="Leading digits of the numbers on this network, space separated — 3 digits for a whole 5xx block, 4 where a block is split. Used to send via a SIM on the recipient's own network, which the operators price lower."
      >
        <textarea
          className="mono"
          rows={3}
          value={prefixes}
          onChange={(e) => setPrefixes(e.target.value)}
          placeholder="543 544 545 5421"
          disabled={!editable}
        />
      </Field>

      <Field label="Balance regex" hint="Must contain a named group (?<amount>…).">
        <input
          className="mono"
          value={form.balanceRegex}
          onChange={(e) => setForm({ ...form, balanceRegex: e.target.value })}
          placeholder="(?i)(?:rs\.?|mur)\s*(?<amount>\d+(?:[.,]\d{1,2})?)"
          disabled={!editable}
        />
      </Field>

      <div className="grid cols-2" style={{ gap: 16 }}>
        <Field label="Expiry regex" hint="Optional. Named group (?<expiry>…).">
          <input
            className="mono"
            value={form.expiryRegex}
            onChange={(e) => setForm({ ...form, expiryRegex: e.target.value })}
            disabled={!editable}
          />
        </Field>
        <Field label="Expiry date format" hint="e.g. dd/MM/yy">
          <input
            className="mono"
            value={form.expiryDateFormat}
            onChange={(e) => setForm({ ...form, expiryDateFormat: e.target.value })}
            disabled={!editable}
          />
        </Field>
      </div>

      <label className="check" style={{ marginBottom: 16 }}>
        <input
          type="checkbox"
          checked={form.isVerified}
          onChange={(e) => setForm({ ...form, isVerified: e.target.checked })}
          disabled={!editable}
        />
        Verified against a real SIM
      </label>

      <div
        style={{
          borderTop: '1px solid rgb(var(--line))',
          paddingTop: 16,
          marginTop: 4,
        }}
      >
        <div className="field-label">Test the pattern</div>
        <div className="field-hint" style={{ marginTop: 0, marginBottom: 8 }}>
          Paste a real +CUSD response and check what the regex extracts before saving it.
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
          <input
            className="grow"
            style={{ flex: 1 }}
            value={sample}
            onChange={(e) => setSample(e.target.value)}
            placeholder="Your balance is Rs 152.50 valid until 30/09/26"
          />
          <button className="btn" onClick={() => test.mutate()} disabled={!sample || test.isPending}>
            Test
          </button>
        </div>
        {test.data && (
          <div className={test.data.success ? 'banner ok' : 'banner danger'} style={{ marginTop: 12 }}>
            <div>
              {test.data.success ? (
                <>
                  Parsed <strong>{test.data.amount}</strong>
                  {test.data.expiry && <> expiring {test.data.expiry}</>}
                </>
              ) : (
                <>Did not parse: {test.data.error}</>
              )}
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}
